import axios, { AxiosResponse } from 'axios';
import { ApiResponse, GetReportResponse, zps_wp } from '../zps';
import { handleDownload } from '../../app/utils/script';

export type Transaction = {
  bankid: number;
  billsInDoubt: number;
  balance?: number;
  cashIn?: MeansCash[];
  cashOut?: MeansCash[];
  cid: number;
  code: number;
  coinsInDoubt: number;
  dateTime: string;
  docCode: string;
  docType: number;
  eid: number;
  entid: number;
  envTotals: number;
  etvid: number;
  gid: number;
  mc: MeansCash[];
  msid: number;
  nseq: number;
  period: number;
  products?: Product[];
  readValues?: ReadValues[];
  shift: number;
  sid: number;
  status: string;
  type: string;
  value: number;
  valueDis: number;
  valueRec: number;
  valueType: string;
  values: Values[];
  uid: number;
  bankingDetails: BankingDetails;
  softVersion?: string;
  equiType: number;
  user: {
    entid: number;
    name: string;
    uid: number;
  };
  typeData?: string;
  cashCollectionData?: CashCollectionData;
};

export type CashCollectionData = {
  periods: {
    period: number;
    paidCash: number;
    loaded: number;
    open?: boolean;
  }[];
  excess?: number;
  invoiced?: number; // total invoiced/paid amount in the period
  prevCumulativeExcess?: number; // last EmptySafecashCollectionData.cumulativeExcess
  cumulativeExcess?: number; // cumulative excess: totalSafe(tb401.value) - billed(sum periods[*].paidCash) + prevCumulativeExcess
  excessWCRemoved?: number; // excess working capital removed
  loaded?: number; // total loaded amount in the period
  beginDate?: number; // first period open date
  endDate?: number; // last period close date
};

export type Product = {
  name: string;
  price: number;
  productid: string;
  quantity: number;
};

export type MeansCash = {
  cid: number;
  index: number;
  origin: number;
  destiny: number;
  quantity: number;
  bucketId: number;
};

export type ReadValues = {
  cid: number;
  cindex: number;
  countId: number;
  rejected: boolean;
  serial: string;
  suspicious: boolean;
  suspiciousErrors: string;
};

export type BankingDetails = {
  card: string;
  account: string;
  depositor: string;
  reason: string;
  declaration?: string;
};

export type Values = {
  cid: number;
  values: number;
  valueRec: number;
  valueDis: number;
};

export interface TransactionResponse extends ApiResponse {
  payment: boolean;
  totalRows: number;
  transactions: Transaction[];
  type: string;
}

export interface FileResult extends ApiResponse {
  fileBytes: string;
  fileName: string;
  type: string;
}

export enum OrderBy {
  DEFAULT = 'default',
  DATE_DESC = 'date_desc'
}

export const fetchTransactionList = (
  controller: AbortController,
  entid: number,
  sid: number,
  eid: number,
  period: number,
  offset: number,
  limit: number,
  totalRows: boolean,
  notesn?: string,
  transactionid?: string,
  begin?: string,
  end?: string,
  uid?: number,
  orderBy?: OrderBy,
  financialOnly?: boolean,
  docCode?: string
): Promise<AxiosResponse<TransactionResponse>> => {
  const url =
    `${zps_wp}/api/transaction?entid=${entid}&sid=${sid}&eid=${eid}&period=${period}&offset=${offset}&limit=${limit}&totalRows=${totalRows}` +
    `${notesn ? `&notesn=${notesn}` : ''}` +
    `${transactionid ? `&transactionid=${transactionid}` : ''}` +
    `${begin ? `&begin=${begin}` : ''}` +
    `${end ? `&end=${end}` : ''}` +
    `${uid ? `&uid=${uid}` : ''}` +
    `${orderBy ? `&orderBy=${orderBy}` : ''}` +
    `${financialOnly ? `&financialOnly=${financialOnly}` : ''}` +
    `${docCode ? `&docCode=${docCode}` : ''}`;
  return axios.get<TransactionResponse>(url, { signal: controller.signal });
};

export const fetchTransactionXLS = async (
  entid: number,
  sid: number,
  eid: number,
  controller: AbortController,
  begin?: string,
  end?: string,
  uid?: number,
  financialOnly?: boolean
): Promise<AxiosResponse<GetReportResponse>> => {
  try {
    const res = await axios.get<GetReportResponse>(
      `${zps_wp}/api/transaction/report/list?format=xls&entid=${entid}&sid=${sid}&eid=${eid}` +
        `${begin ? '&begin=' + begin : ''}` +
        `${end ? '&end=' + end : ''}` +
        `${uid ? '&uid=' + uid : ''}` +
        `${financialOnly ? `&financialOnly=${financialOnly}` : ''}`,
      { signal: controller.signal }
    );

    if (res.data?.report) {
      handleDownload(res.data.report, res.data.name, res.data.mime);
    }

    return res;
  } catch (error) {
    throw error; // IMPORTANT: lets handleApiError catch it
  }
};

export const fetchTransactionReport = (
  controller: AbortController,
  eid: number,
  period: number,
  code: number
): Promise<AxiosResponse<GetReportResponse>> => {
  return axios
    .get<GetReportResponse>(`${zps_wp}/api/transaction/report?format=pdf&eid=${eid}&period=${period}&code=${code}`, {
      signal: controller.signal
    })
    .then(res => {
      if (res.data?.statusOper?.code === 0 && res.data.report) {
        handleDownload(res.data.report, res.data.name, res.data.mime);
      }
      return res;
    });
};

export const fetchTransactionLog = async (
  controller: AbortController,
  eid: number,
  period: number,
  code: number
): Promise<AxiosResponse<FileResult>> => {
  try {
    const res = await axios.get<FileResult>(`${zps_wp}/api/transaction/log?eid=${eid}&period=${period}&code=${code}`, {
      signal: controller.signal
    });

    if (res.data?.fileBytes && res.data?.fileName && res.data.statusOper?.code === 0) {
      handleDownload(res.data.fileBytes, res.data.fileName, '.zip');
    }
    return res;
  } catch (error) {
    throw error; // IMPORTANT: lets handleApiError catch it
  }
};

export const fetchCashCollectionList = (
  controller: AbortController,
  entid: number,
  sid: number,
  eid: number,
  offset: number,
  limit: number,
  totalRows: boolean,
  notesn?: string,
  transactionid?: string,
  begin?: string,
  end?: string,
  uid?: number,
  orderBy?: OrderBy
): Promise<AxiosResponse<TransactionResponse>> => {
  const url =
    `${zps_wp}/api/cashCollections?entid=${entid}&sid=${sid}&eid=${eid}&offset=${offset}&limit=${limit}&totalRows=${totalRows}` +
    `${notesn ? `&notesn=${notesn}` : ''}` +
    `${transactionid ? `&transactionid=${transactionid}` : ''}` +
    `${begin ? `&begin=${begin}` : ''}` +
    `${end ? `&end=${end}` : ''}` +
    `${uid ? `&uid=${uid}` : ''}` +
    `${orderBy ? `&orderBy=${orderBy}` : ''}`;

  return axios.get<TransactionResponse>(url, { signal: controller.signal });
};

export const fetchCashCollectionXLS = async (
  entid: number,
  sid: number,
  eid: number,
  controller: AbortController,
  begin?: string,
  end?: string,
  uid?: number
): Promise<AxiosResponse<GetReportResponse>> => {
  try {
    const res = await axios.get<GetReportResponse>(
      `${zps_wp}/api/cashCollections/report/list?format=xls&entid=${entid}&sid=${sid}&eid=${eid}` +
        `${begin ? '&begin=' + begin : ''}` +
        `${end ? '&end=' + end : ''}` +
        `${uid ? '&uid=' + uid : ''}`,
      { signal: controller.signal }
    );
    if (res.data?.report) {
      handleDownload(res.data.report, res.data.name, res.data.mime);
    }
    return res;
  } catch (error) {
    throw error; // IMPORTANT: lets handleApiError catch it
  }
};

export const fetchCashCollectionReport = async (
  controller: AbortController,
  eid: number,
  period: number,
  code: number
): Promise<AxiosResponse<GetReportResponse>> => {
  try {
    const res = await axios.get<GetReportResponse>(
      `${zps_wp}/api/cashCollections/report?format=pdf&eid=${eid}&period=${period}&code=${code}`,
      {
        signal: controller.signal
      }
    );
    if (res.data?.statusOper?.code === 0 && res.data.report) {
      handleDownload(res.data.report, res.data.name, res.data.mime);
    }
    return res;
  } catch (error) {
    throw error; // IMPORTANT: lets handleApiError catch it
  }
};
